/**
 * Single source of truth for institutional access decisions.
 * ALL components (SubscriptionRoute, ProtectedRoute, Dashboard) MUST use these
 * functions instead of duplicating logic.
 *
 * Architecture rules enforced here:
 * - unknown ≠ denied
 * - degraded institutional = valid access
 * - UI partial rendering > infinite spinner
 * - No global invalidations allowed
 */

import * as Sentry from "@sentry/react";

import type { StatusReliability } from "@/hooks/useInstitutionalStudent";

// ─── Access State Enum ───────────────────────────────────────────────────────

export type AccessState =
  | "unknown"         // Data not yet loaded — do NOT treat as denied
  | "loading"         // Actively fetching — show spinner only for blocking deps
  | "granted"         // Full confirmed access
  | "granted_degraded" // Role confirmed, details failed — still grant access
  | "denied"          // Explicitly denied (no subscription, cohort ended, etc.)
  | "error_non_blocking"; // Non-critical fetch failed — show partial UI

// ─── Institutional Access ────────────────────────────────────────────────────

export interface InstitutionalAccessInput {
  isInstitutionalStudent: boolean;
  isProgramActive: boolean;
  statusReliability: StatusReliability;
  cohortEnded: boolean;
}

export interface AccessDecision {
  /** true = user should bypass paywall */
  hasInstitutionalAccess: boolean;
  /** human-readable reason for logging */
  reason: string;
  /** explicit access state */
  state: AccessState;
}

/**
 * Determines whether an institutional student should bypass the paywall.
 *
 * Rules:
 * 1. Not institutional → no bypass
 * 2. Program active → bypass
 * 3. Status degraded (role confirmed, details failed) → bypass (don't lock out)
 * 4. Cohort ended → no bypass (falls back to personal subscription)
 * 5. Institutional but program not started → grant access (pre-program)
 */
export function resolveInstitutionalAccess(input: InstitutionalAccessInput): AccessDecision {
  const { isInstitutionalStudent, isProgramActive, statusReliability, cohortEnded } = input;

  if (!isInstitutionalStudent) {
    return { hasInstitutionalAccess: false, reason: "not_institutional", state: "denied" };
  }

  if (isProgramActive) {
    return { hasInstitutionalAccess: true, reason: "program_active", state: "granted" };
  }

  if (statusReliability === "degraded") {
    return {
      hasInstitutionalAccess: true,
      reason: "degraded_status_safe_bypass",
      state: "granted_degraded",
    };
  }

  if (cohortEnded) {
    return { hasInstitutionalAccess: false, reason: "cohort_ended", state: "denied" };
  }

  // Fallback: institutional but program not started yet — grant access
  return { hasInstitutionalAccess: true, reason: "institutional_default_grant", state: "granted" };
}

// ─── Loading Decisions ───────────────────────────────────────────────────────

/**
 * Determines whether the loading state should block rendering
 * in SubscriptionRoute (paywall gate).
 *
 * Blocking deps: auth, admin role, subscription.
 * Non-blocking: institutional details (if subscription already loaded).
 */
export interface LoadingDecisionInput {
  authLoading: boolean;
  adminLoading: boolean;
  subscriptionLoaded: boolean;
  institutionalLoading: boolean;
}

export function shouldBlockOnLoading(input: LoadingDecisionInput): {
  block: boolean;
  reason: string;
  state: AccessState;
} {
  if (input.authLoading) return { block: true, reason: "auth_loading", state: "loading" };
  if (input.adminLoading) return { block: true, reason: "admin_loading", state: "loading" };
  if (!input.subscriptionLoaded) return { block: true, reason: "subscription_not_loaded", state: "loading" };
  // Institutional loading only blocks if subscription hasn't loaded yet
  if (input.institutionalLoading && !input.subscriptionLoaded) {
    return { block: true, reason: "institutional_loading_no_sub", state: "loading" };
  }
  return { block: false, reason: "all_loaded", state: "granted" };
}

/**
 * Dashboard-specific loading decision.
 * 
 * Blocking: profile, module progress.
 * Non-blocking: institutional details (show partial UI if slow),
 *               streak, stats, compliance, review bank.
 */
export interface DashboardLoadingInput {
  profileLoading: boolean;
  progressLoading: boolean;
  institutionalLoading: boolean;
}

export function shouldBlockDashboard(input: DashboardLoadingInput): {
  block: boolean;
  reason: string;
} {
  if (input.profileLoading) return { block: true, reason: "profile_loading" };
  if (input.progressLoading) return { block: true, reason: "progress_loading" };
  // Institutional loading is NON-BLOCKING for dashboard — show partial UI
  return { block: false, reason: "ready" };
}

export interface DashboardRouteInput {
  isAdmin: boolean;
  institutionalDecision: AccessDecision;
  cohortEnded: boolean;
  /** Subscription/access layer fallback while institutional derivation is revalidating */
  hasCohortAccess: boolean;
}

export interface DashboardRouteDecision {
  dashboardType: "admin" | "institutional" | "regular";
  reason: string;
}

export function resolveDashboardRoute(input: DashboardRouteInput): DashboardRouteDecision {
  const { isAdmin, institutionalDecision, cohortEnded, hasCohortAccess } = input;

  if (isAdmin) {
    return { dashboardType: "admin", reason: "admin_dashboard" };
  }

  if (institutionalDecision.hasInstitutionalAccess && !cohortEnded) {
    return {
      dashboardType: "institutional",
      reason: `institutional_${institutionalDecision.reason}`,
    };
  }

  if (hasCohortAccess && !cohortEnded) {
    return {
      dashboardType: "institutional",
      reason: "institutional_subscription_context_fallback",
    };
  }

  if (cohortEnded) {
    return { dashboardType: "regular", reason: "regular_cohort_ended" };
  }

  return {
    dashboardType: "regular",
    reason: `regular_${institutionalDecision.reason}`,
  };
}

// ─── Protected Route Decisions ───────────────────────────────────────────────

export interface ProtectedRouteInput {
  isInstitutionalStudent: boolean;
  isProgramActive: boolean;
  isProgramNotStarted: boolean;
  isBaselineRequired: boolean;
  cohortEnded: boolean;
  isAdmin: boolean;
  pathname: string;
}

export interface RouteDecision {
  allow: boolean;
  redirectTo: string | null;
  reason: string;
}

// Routes always allowed for institutional students
const ALWAYS_ALLOWED_ROUTES = ['/dashboard', '/settings', '/messages', '/faq', '/real-exam'];

// Academic routes that require baseline completion
const ACADEMIC_ROUTES = ['/quiz', '/study', '/exam-simulation', '/review-bank', '/visual-anatomy'];

// Routes blocked during active program (personal-only modes)
const PERSONAL_ONLY_ROUTES = ['/study', '/exam-simulation', '/review-bank', '/visual-anatomy'];

/**
 * Determines whether an institutional student should be allowed to access a route.
 * Only called when isInstitutionalStudent && !isAdmin.
 */
export function resolveInstitutionalRoute(input: ProtectedRouteInput): RouteDecision {
  const { isProgramActive, isProgramNotStarted, isBaselineRequired, cohortEnded, pathname } = input;

  const isAlwaysAllowed = ALWAYS_ALLOWED_ROUTES.some(r => pathname === r || pathname.startsWith(r + '/'));
  const isAcademicRoute = ACADEMIC_ROUTES.some(r => pathname === r || pathname.startsWith(r + '/'));
  const isPersonalOnlyRoute = PERSONAL_ONLY_ROUTES.some(r => pathname === r || pathname.startsWith(r + '/'));
  const isAdminInstitutionRoute = pathname.startsWith('/institution/');

  // Cohort ended → full personal mode, allow everything
  if (cohortEnded) {
    return { allow: true, redirectTo: null, reason: "cohort_ended_personal_mode" };
  }

  // Program active or not yet started — apply restrictions
  // (cohortEnded already returned above, so this is always the non-ended path)

  // Block students from admin institution dashboard
  if (isAdminInstitutionRoute) {
    return { allow: false, redirectTo: '/dashboard', reason: "student_blocked_from_admin_dashboard" };
  }

  // BASELINE GATE: block academic routes until baseline is completed
  if (isBaselineRequired && isAcademicRoute && isProgramActive && !isProgramNotStarted) {
    return { allow: false, redirectTo: '/dashboard', reason: "baseline_required" };
  }

  // Always-allowed routes pass through
  if (isAlwaysAllowed) {
    return { allow: true, redirectTo: null, reason: "always_allowed_route" };
  }

  // Personal-only routes blocked during active program
  if (isPersonalOnlyRoute && isProgramActive) {
    return { allow: false, redirectTo: '/dashboard', reason: "personal_route_blocked_during_program" };
  }

  // Quiz route during active program (not personal-only) → allowed
  if (isAcademicRoute && isProgramActive) {
    return { allow: true, redirectTo: null, reason: "academic_route_allowed" };
  }

  // Any other route → allow
  return { allow: true, redirectTo: null, reason: "default_allow" };
}

// ─── Observability ───────────────────────────────────────────────────────────

/**
 * Logs access gate decisions for observability.
 * Call this in SubscriptionRoute / ProtectedRoute / Dashboard when a gate decision is made.
 */
export function logAccessDecision(
  component: string,
  decision: string,
  details: Record<string, unknown>
) {
  console.log(
    `🛡️ [${component}] Access decision: ${decision}`,
    JSON.stringify(details, null, 0)
  );

  // Send as Sentry breadcrumb for post-mortem debugging
  Sentry.addBreadcrumb({
    category: "access-gate",
    message: `${component}: ${decision}`,
    data: details,
    level: decision.includes("PAYWALL") || decision.includes("DENIED") ? "warning" : "info",
  });
}

// ─── Invalidation Policy ─────────────────────────────────────────────────────

/**
 * Allowed query keys that useAuth can invalidate on user change.
 * NEVER use queryClient.invalidateQueries() without a queryKey filter.
 * This list is the exhaustive set of keys that should refresh on user change.
 */
export const ALLOWED_AUTH_INVALIDATION_KEYS = [
  "profile",
  "moduleProgress",
  "institutionalStudent",
] as const;

/**
 * Events that justify query invalidation.
 * Anything else should NOT trigger invalidation.
 */
export const VALID_INVALIDATION_EVENTS = [
  "login",
  "logout",
  "subscription_change",
  "cohort_change",
  "baseline_completed",
  "level_completed",
  "explicit_mutation",
] as const;
